import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

/**
 * OptimizedImage component for better image loading performance
 * Uses original image formats (PNG) with lazy loading
 * 
 * @param {Object} props - Component props
 * @param {string} props.src - Image source path
 * @param {string} props.alt - Alt text for the image
 * @param {string} [props.className] - Additional CSS classes
 * @param {boolean} [props.lazy=true] - Whether to lazy load the image
 * @returns {React.ReactElement} OptimizedImage component
 */
const OptimizedImage = ({
  src,
  alt,
  className = '',
  lazy = true,
  ...props
}) => {
  const [loaded, setLoaded] = useState(!lazy);
  const [inView, setInView] = useState(!lazy);
  const imageRef = useRef(null);
  
  // Check if image is in viewport for lazy loading
  useEffect(() => {
    if (!lazy || inView) return;
    
    const checkInView = () => {
      if (!imageRef.current) return;
      
      const rect = imageRef.current.getBoundingClientRect();
      const buffer = 200; // 200px buffer
      
      const isVisible = (
        rect.top <= (window.innerHeight || document.documentElement.clientHeight) + buffer &&
        rect.left <= (window.innerWidth || document.documentElement.clientWidth) + buffer &&
        rect.bottom >= 0 - buffer &&
        rect.right >= 0 - buffer
      );
      
      if (isVisible) {
        setInView(true);
      }
    };
    
    // Check immediately
    checkInView();
    
    // Add scroll and resize listeners
    window.addEventListener('scroll', checkInView, { passive: true });
    window.addEventListener('resize', checkInView, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', checkInView);
      window.removeEventListener('resize', checkInView);
    };
  }, [lazy, inView]);
  
  // Handle image load
  const handleLoad = () => {
    setLoaded(true);
  };
  
  return (
    <div
      ref={imageRef}
      className={`relative overflow-hidden ${className}`}
      {...props}
    >
      {/* Low quality placeholder for lazy loading */}
      {lazy && !loaded && (
        <div 
          className="absolute inset-0 bg-gray-200 animate-pulse"
          aria-hidden="true"
        />
      )}
      
      {/* Main image */}
      {(!lazy || inView) && (
        <img
          src={src}
          alt={alt}
          className={`transition-opacity duration-500 ${
            loaded ? 'opacity-100' : 'opacity-0'
          } w-auto h-auto`}
          style={{ maxWidth: '100%', maxHeight: '100%', ...props.style }}
          onLoad={handleLoad}
          loading={lazy ? 'lazy' : 'eager'}
          decoding="async"
        />
      )}
    </div>
  );
};

OptimizedImage.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  className: PropTypes.string,
  lazy: PropTypes.bool
};

export default OptimizedImage;
